"use client";

import { Button } from "@/components/ui/button";
import { Trash2, Mail } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import type { InvitesOutput } from "@/interfaces/invites";
import { UserRoles, userRolesTranslations } from "@/interfaces";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface InvitedListProps {
  isOwner: boolean;
  invitedUsers: InvitesOutput[];
  onCancelInvite: (inviteId: string) => void;
}

export function InvitedList({
  isOwner,
  invitedUsers,
  onCancelInvite,
}: Readonly<InvitedListProps>) {
  const isExpired = (date: Date) => {
    return new Date(date) < new Date();
  };

  const getRoleBadgeColor = (role: UserRoles) => {
    switch (role) {
      case UserRoles.OWNER:
        return "bg-amber-100 text-amber-800 border-amber-200";
      case UserRoles.ADMIN:
        return "bg-blue-100 text-blue-800 border-blue-200";
      case UserRoles.MANAGER:
        return "bg-purple-100 text-purple-800 border-purple-200";
      case UserRoles.MEMBER:
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="w-full mt-4">
      <div className="flex items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Convites Pendentes
        </h2>
        <div className="ml-3 bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded-full">
          {invitedUsers.length} convites
        </div>
      </div>

      <div className="hidden sm:grid grid-cols-[3fr,2fr,2fr,auto] gap-4 mb-2 font-semibold text-gray-600 px-4">
        <div>Email</div>
        <div>Função</div>
        <div>Expira em</div>
        <div className="text-right">Ações</div>
      </div>

      <div className="space-y-3">
        {invitedUsers?.map((invite) => {
          const expired = isExpired(invite.expiresAt);

          return (
            <div
              key={invite.id}
              className={`flex flex-col sm:grid sm:grid-cols-[3fr,2fr,2fr,auto] gap-2 sm:gap-4 items-start sm:items-center p-4 rounded-md border ${
                expired
                  ? "bg-red-50 border-red-200"
                  : "hover:bg-gray-50 border-gray-200"
              } transition-colors`}
            >
              <div className="flex items-center space-x-2 w-full sm:w-auto">
                <Mail
                  className={`h-4 w-4 ${
                    expired ? "text-red-500" : "text-blue-500"
                  }`}
                />
                <p
                  className="text-sm truncate font-medium"
                  title={invite.userEmail}
                >
                  {invite.userEmail}
                </p>
              </div>

              <div className="flex items-start">
                <span
                  className={`inline-block px-2.5 py-1 rounded-md text-xs font-medium ${getRoleBadgeColor(
                    invite.role
                  )}`}
                >
                  {userRolesTranslations[invite.role]}
                </span>
              </div>

              <div className="flex items-center">
                <p
                  className={`text-sm ${
                    expired ? "text-red-600 font-medium" : "text-gray-500"
                  }`}
                >
                  {expired
                    ? "Expirado"
                    : format(new Date(invite.expiresAt), "dd 'de' MMMM", {
                        locale: ptBR,
                      })}
                </p>
              </div>

              <div className="flex justify-end w-full sm:w-auto mt-2 sm:mt-0 sm:ml-auto">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="p-0 h-8 w-8"
                      disabled={!isOwner}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Cancelar convite</span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Confirmar cancelamento
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja cancelar o convite para{" "}
                        <span className="font-medium">{invite.userEmail}</span>?
                        Esta ação não pode ser desfeita.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => onCancelInvite(invite.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Confirmar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          );
        })}
      </div>

      {invitedUsers.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
          <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Nenhum convite pendente</p>
          <p className="text-gray-400 text-sm mt-1">
            Convide novos membros usando o formulário acima
          </p>
        </div>
      )}
    </div>
  );
}
